import React, {useContext, useRef, useState, useEffect, useCallback} from 'react';
import {Container} from "react-bootstrap";
import PageHeader from "components/PageHeader";
import AgGridToolbar from "components/AgGridToolbar/AgGridToolbar";
import {AgGridReact} from "ag-grid-react";
import {
    DEFAULT_GRID_PAGINATION_SIZE,
    ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE,
    GRID_COLUMN_PROPERTY
} from "utils/Constants";
import {
    dateToString,
    stringToDate
} from "utils/FormatUtils";
import useWindowDimensions from "hooks/Dimensions";
import {SwiftTransactionContext} from "./context";
import {createExcelExportParams} from "utils/ExcelExportUtils";
import {UtilsContext} from "../../../context/utils";
import {ActionPanelContext} from "context/action";
import * as URL from "service/UrlConstant";
import {SecurityContext} from "context/security";
import {ToastContext} from "context/Toast";
import {EntityAccountsContext} from "components/entity-accounts/context";
import {
    addPropertiesToGridColumns,
    getDisplayedNumberOfGridRows,
    getSelectedRows
} from "../../../utils/gridColumnUtils";
import ScrollButton from "../../../components/AgGridToolbar/ToolBarButtons/ScrollButton";
import {normaliseFilterValueAllToEmptyString} from "../../../utils/StringUtils";
import {
    SWIFT_TRANSACTION_URL_SEARCH
} from "service/UrlConstant";

export default function SwiftTransactionResultPanel() {

    const {swiftTransactionResultsDisplayState, setSwiftTransactionResultsDisplayState,
        showGridToolbar, setShowGridToolbar, swiftTransactionPanelFilterState, displaySwiftTransactionPanelPage, TEMPLATE_PANEL_SEARCH_PAGE, selectedAnyCheckbox,setSelectedAnyCheckbox, selectedRows,
        setSelectedRows} = useContext(SwiftTransactionContext)
    const {hiddenExportGridApi, showButton, setShowButton, paginationPageSize, setPaginationPageSize} = useContext(UtilsContext);
    const { makePutRequest, makePostRequest} = useContext(SecurityContext);
    const {success, error} = useContext(ToastContext);
    const {setHandleSwiftTransactionCompleteFunction, setHandleSwiftTransactionRejectFunction,
        setHandleExportFunction, setHandleRefreshFunction,
        setIsValidApprover, isValidApprover,
        setIsValidRejecter, isValidRejecter
    } = useContext(ActionPanelContext)

    const gridRef = useRef();
    const swiftTransactionResultsDisplayStateRef = useRef(swiftTransactionResultsDisplayState);
    const swiftTransactionPanelFilterStateRef = useRef(swiftTransactionPanelFilterState);
    // Guards the one-time initial data load on mount so it does not re-fire on re-renders.
    const hasAutoLoadedRef = useRef(false);
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [columnDefs, setColumnDefs] = useState([]);

    const {isMobile} = useWindowDimensions();

    const columnTypes ={
        flexColType: {
            sortable: false,
            resizable: false,
            floatingFilter: false,
            filter: false,
            enableCellChangeFlash: false,
            suppressMenu:true,
            flex:1,
            suppressMovable:true,
        },
        FlexHeightColType: {
            sortable: false,
            resizable: false,
            floatingFilter: false,
            filter: false,
            enableCellChangeFlash: false,
            lockPinned:!!isMobile,
            autoHeight: true,
            wrapText: true,
            minWidth:300

        }
    }

    const defaultColDef = {
        sortable:true,
        resizable:true,
        floatingFilter:true,
        filter:true,
        enableCellChangeFlash:true,
        lockPinned:!!isMobile,
    };

    useEffect(() => {
        swiftTransactionResultsDisplayStateRef.current = swiftTransactionResultsDisplayState;
    }, [swiftTransactionResultsDisplayState]);

    useEffect(() => {
        swiftTransactionPanelFilterStateRef.current = swiftTransactionPanelFilterState;
    }, [swiftTransactionPanelFilterState]);


    const getActiveSwiftSearchParams = (fallbackEffective) => {
        const currentResultsState = swiftTransactionResultsDisplayStateRef.current || {};
        const currentFilterState = swiftTransactionPanelFilterStateRef.current || {};
        const activeSearch = currentResultsState.wireSearch || {};

        const effectiveFromFilter = currentFilterState.effective ? dateToString(currentFilterState.effective) : null;
        return {
            indStatus: activeSearch.indStatus ?? normaliseFilterValueAllToEmptyString(currentFilterState.status),
            transId: activeSearch.idPmgMQSwift ?? currentFilterState.transId,
            cdMtType: activeSearch.cdMtType ?? normaliseFilterValueAllToEmptyString(currentFilterState.msgType),
            dtEffective: dateToString(swiftTransactionResultsDisplayState.wireSearch.dtEffective) ?? effectiveFromFilter
        };
    };

    const loadSwiftTransaction = (fallbackEffective)=>{

        const params = getActiveSwiftSearchParams(fallbackEffective);

        const onSuccessResponse = (response) => {

            const SwiftTransactionDisplayState = JSON.parse(JSON.stringify(swiftTransactionResultsDisplayStateRef.current || {}))
            SwiftTransactionDisplayState.resData = response.data
            SwiftTransactionDisplayState.wireSearch = params
            setSwiftTransactionResultsDisplayState(SwiftTransactionDisplayState);
            displaySwiftTransactionPanelPage(TEMPLATE_PANEL_SEARCH_PAGE);
        }
        const onErrorResponse = (err) => {
            console.error(err);
            error("No Swift Transactions found matching your search")
        }
        makePostRequest(params,
            SWIFT_TRANSACTION_URL_SEARCH , onSuccessResponse, onErrorResponse);
    }

    // Load data once on initial mount using the default filter values (status/msgType = ALL -> '',
    // empty transId, default effective date). Reuses loadSwiftTransaction so the default-load
    // mirrors the filter's Search exactly. The ref guard ensures it does not re-fire on re-renders
    // and does not interfere with the user's Search/Refresh/Complete/Reject flows.
    useEffect(() => {
        if (hasAutoLoadedRef.current) {
            return;
        }
        hasAutoLoadedRef.current = true;
        loadSwiftTransaction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const swiftTransactionComplete = (userSelectedRow, onSuccess, onError, completeComment)=>{

        const params = {
            idPmgMQSwift: userSelectedRow?.[0]?.idPmgMQSwift,
            idVersion: userSelectedRow?.[0]?.idVersion,
            cdClient: userSelectedRow?.[0]?.cdClient,
            cdPaymentRefNum: normaliseFilterValueAllToEmptyString(completeComment)
        }

        const onSuccessResponse = () => {
            if (onSuccess) onSuccess();
            success("Swift Transaction was completed successfully");

            setSelectedAnyCheckbox(false)
            setSelectedRows([]);

            loadSwiftTransaction(userSelectedRow?.[0]?.dtEffective);
        }

        const onErrorResponse = (err) => {
            console.error(err);
            if (onError) onError(err);
            const responseData = err.response?.data;
            let errMsg = "An error occurred while completing the Swift Transaction";
            if (typeof responseData === 'string') {
                errMsg = responseData;
            } else if (responseData && typeof responseData === 'object') {
                errMsg = Object.values(responseData).join(', ');
            }
            error(errMsg);
        }

        makePostRequest(params, URL.SWIFT_TRANSACTION_URL_COMPLETE, onSuccessResponse, onErrorResponse);
    }

    const swiftTransactionReject = (userSelectedRow, onSuccess, onError)=>{

        const params = {
            idPmgMQSwift: userSelectedRow?.[0]?.idPmgMQSwift,
            idVersion: userSelectedRow?.[0]?.idVersion,
            cdClient: userSelectedRow?.[0]?.cdClient,
        }

        const onSuccessResponse = () => {
            if (onSuccess) onSuccess();
            success("Swift Transaction was rejected successfully");

            setSelectedAnyCheckbox(false)
            setSelectedRows([]);

            loadSwiftTransaction(userSelectedRow?.[0]?.dtEffective);
        }

        const onErrorResponse = (err) => {
            console.error(err);
            if (onError) onError(err);
            const responseData = err.response?.data;
            let errMsg = "An error occurred while rejecting the Swift Transaction";
            if (typeof responseData === 'string') {
                errMsg = responseData;
            } else if (responseData && typeof responseData === 'object') {
                errMsg = Object.values(responseData).join(', ');
            }
            error(errMsg);
        }

        makePostRequest(params, URL.SWIFT_TRANSACTION_URL_REJECT, onSuccessResponse, onErrorResponse);
    }

    // Wire handlers to context when gridApi is available
    useEffect(() => {
        setHandleExportFunction({handleExport:exportQueryList, gridApi: gridApi});
        setHandleSwiftTransactionCompleteFunction({handleSwiftTransactionComplete: swiftTransactionComplete, gridApi: gridApi})
        setHandleSwiftTransactionRejectFunction({handleSwiftTransactionReject: swiftTransactionReject, gridApi: gridApi})
        setHandleRefreshFunction({handleRefresh: handleRefreshActionClicked, gridApi:null });
    }, [gridApi])

    // Update row data when search results change
    useEffect(() => {
        if (swiftTransactionResultsDisplayState?.resData) {
            setRowData(swiftTransactionResultsDisplayState.resData);
            setShowGridToolbar(swiftTransactionResultsDisplayState.resData.length > 0);
        }
    }, [swiftTransactionResultsDisplayState?.resData])


    const exportQueryList = (type, rowData) => {
        const columnDef = columnDefsResultsPanel.filter(col => col.headerName !== 'Select')
        const exportRows = rowData || swiftTransactionResultsDisplayState?.resData || [];

        //Set Column definition
        hiddenExportGridApi.current.api.setColumnDefs(columnDef);
        //Set Data
        hiddenExportGridApi.current.api.setRowData(exportRows);
        //Export as csv/excel
        type==='EXCEL' ?
            hiddenExportGridApi.current.api.exportDataAsExcel(createExcelExportParams('Wires')) :
            hiddenExportGridApi.current.api.exportDataAsCsv()
    }

    const handleRefreshActionClicked = (refreshCriteria ,gridApi , onSuccess, onError) => {
        handleRefresh(refreshCriteria)
    };

    const handleRefresh = (refreshCriteria) => {
        if (!swiftTransactionResultsDisplayStateRef.current) return;
        const params = getActiveSwiftSearchParams();

        const onSuccessResponse = (response) => {
            setShowGridToolbar(response.data.length > 0)

            const swiftTransactionDisplayState = JSON.parse(JSON.stringify(swiftTransactionResultsDisplayState))
            swiftTransactionDisplayState.resData = ([...response.data])
            swiftTransactionDisplayState.wireSearch = params
            setSwiftTransactionResultsDisplayState(swiftTransactionDisplayState);
            displaySwiftTransactionPanelPage(TEMPLATE_PANEL_SEARCH_PAGE);
        }
        const onErrorResponse = (err) => {
            console.error(err);
            error("Error refreshing swift transactions data")
        }

        makePostRequest(params,
            URL.SWIFT_TRANSACTION_URL_SEARCH , onSuccessResponse, onErrorResponse);
    }

    const LogItem = function (params){
        return(<>
            {
                params.value.toString().split("\r\n").map( (log, index) => {
                    return (<React.Fragment key={`log-line-${index}`}>{log}<br /></React.Fragment>)
                })}
        </>);
    }

    const logRenderer = function (props){
        if(!props.data.comments)
            return (<></>)
        return <LogItem value = {props.data.comments}/>
    }

    const instructionsRenderer = function (props){
        if(!props.data.wireInstructions)
            return (<></>)
        return <LogItem value ={props.data.wireInstructions}/>
    }


    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    }

    const onDataRendered = (params) => {
        params.columnApi.autoSizeAllColumns();
        setShowButton(getDisplayedNumberOfGridRows(gridApi) >= ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE);
    }

    const onPaginationChanged = () => {
        setShowButton(getDisplayedNumberOfGridRows(gridApi) >= ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE);
    }

    function pageTitle(){
        return "Swift Transaction Details"
    }

    const onRowSelected = (row) => {

        if(!row.node.selected || row.data.isApproveEnabled !== isValidApprover) {
            setIsValidApprover(true)

            gridApi.getSelectedRows()
                .filter(row => !row.isApproveEnabled)
                .map(row => {
                        setIsValidApprover(row.isApproveEnabled);
                    }
                );
        }

        if(!row.node.selected || row.data.isRejectEnabled !== isValidRejecter){
            setIsValidRejecter(true)

            gridApi.getSelectedRows()
                .filter(row => !row.isRejectEnabled)
                .map(row => {
                        setIsValidRejecter(row.isRejectEnabled);
                    }
                );
        }

    }

    //const isRowSelectable = rowNode => rowNode.data ? (rowNode.data. && rowNode.data.status.length > 0) : false;
    // !['PEND', 'TRD''].includes(params.data.uiStatus) (!!params.data) && params.data.status === 'ACCEPT'
    const isRowSelectable = useCallback((params) => {
        return (!!params.data) && ['PEND', 'TRD'].includes(params.data.indStatus);
    }, []);

    const updatedColumnDefs = addPropertiesToGridColumns(columnDefsResultsPanel, GRID_COLUMN_PROPERTY)

    const onSelectionChanged = useCallback((params)=>{
        const userSelectedRows = params.api.getSelectedRows();
        setSelectedAnyCheckbox(userSelectedRows.length > 0)
    },[])

    const handleSwiftTransactionComplete = ()=>{
        const selected = getSelectedRows(gridApi);
        setSelectedRows(selected);
    }

    return(<>
        <Container fluid>
            <PageHeader text={pageTitle()}>
                {/*<WiresParamsBadge/>*/}
            </PageHeader>

            { showGridToolbar && <AgGridToolbar
                swiftTransactionScreen={true}
                gridApi={gridApi}
                gridColumnApi={gridColumnApi}
                columnDefs={columnDefs}
                setColumnDefs={setColumnDefs}
                rowData={rowData}
                paginationPageSize={paginationPageSize}
                setPaginationPageSize={setPaginationPageSize}
                onRefresh={handleRefresh}
                selectedAnyCheckbox={selectedAnyCheckbox}
                selectedRows={selectedRows}
                handleSwiftTransactionComplete={handleSwiftTransactionComplete}
            >
            </AgGridToolbar>}
            <div className="ag-theme-alpine">
                <AgGridReact
                    ref={gridRef}
                    domLayout={'autoHeight'}
                    frameworkComponents={{
                        logRenderer: logRenderer,
                        instructionsRenderer: instructionsRenderer,
                    }}
                    paginationPageSize={DEFAULT_GRID_PAGINATION_SIZE}
                    groupDefaultExpanded={'1'}
                    enableRowGroup={false}
                    suppressDragLeaveHidesColumns={true}
                    suppressMakeColumnVisibleAfterUnGroup={true}
                    suppressModelUpdateAfterUpdateTransaction={true}
                    suppressScrollOnNewData={true}
                    suppressAggFuncInHeader={true}
                    allowShowChangeAfterFilter={false}
                    rowGroupPanelShow={'never'}
                    enableSorting={true}
                    enableFilter={true}
                    pagination={true}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onDataRendered}
                    onPaginationChanged={onPaginationChanged}
                    rowDataUpdated={onDataRendered}
                    defaultColDef={defaultColDef}
                    columnDefs={updatedColumnDefs}
                    columnTypes={columnTypes}
                    rowData={rowData}
                    animateRows={true}
                    rowSelection={'single'}
                    onRowSelected={onRowSelected}
                    isRowSelectable={isRowSelectable}
                    onSelectionChanged={onSelectionChanged}
                    suppressRowClickSelection={false}
                    popupParent={document.body}
                >
                </AgGridReact>
            </div>
        </Container>
        {showButton && <ScrollButton showButton={showButton}/>}
    </>);
}

export const columnDefsResultsPanel = [
    {
        headerName: '',
        headerCheckboxSelection: false,
        field: "checkbox",
        minWidth: 120,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        cellClassRules: {
            "checkbox-disabled": (params) => {
                return !params.data || !['PEND', 'TRD'].includes(params.data.indStatus);
            },
        }
    },
    {headerName: 'Trans Id', field: 'idPmgMQSwift',hide:true, colId: 'id', suppressColumnsToolPanel:true},
    {headerName: 'TD Ref Num', field: 'cdPaymentRefNum'},
    {headerName: 'Status', field: 'indStatus'},
    {headerName: 'Effective', field: 'dtEffective', valueGetter: (params)=>stringToDate(dateToString(params.data.dtEffective))},
    {headerName: 'Client', field: 'cdClient'},
    {headerName: 'Msg Type', field: 'cdMtType'},
    {headerName: 'Action', field: 'cdMtTypeDesc'},
    {headerName: 'Security', field: 'cdSecurity'},
    {headerName: 'Quantity', field: 'cdQuantity'},
    {headerName: 'Currency', field: 'cdCurrency'},
    {headerName: 'Price', field: 'cdPrice'},
    {headerName: 'Amount', field: 'cdAmount'},
    {headerName: 'Settlement Location', field: 'cdSettlementLocation'},
    {headerName: 'Receiving Party', field: 'cdRcvParty'},
    {headerName: 'Delivering Party', field: 'cdDlvParty'},
    {headerName: 'CounterParty Account', field: 'cdCpty'},
    {headerName: 'Settlement Instruction', field: 'cdSettlementInst'},
    {headerName: 'Full Message', field: 'cdMsg'},
    {headerName: '', field: '', type: 'flexColType', suppressColumnsToolPanel: true},
];
